import { createSignal } from "solid-js";
import { msg } from "@lingui/core/macro";
import type { PowerSyncDrizzleDB } from "@listto/powersync";
import { deleteTodoMutation, restoreTodoMutation, updateTodoMutation } from "@listto/shared";
import { useUndo } from "@/lib/undo";
import { useI18n } from "@/providers/i18n-provider";

const EXIT_DURATION_MS = 200;

interface UseTodoSelectionOptions {
  drizzle: () => PowerSyncDrizzleDB | null;
  setExitingIds: (updater: (prev: Set<string>) => Set<string>) => void;
}

export function useTodoSelection(options: UseTodoSelectionOptions) {
  const { t } = useI18n();
  const { registerUndo } = useUndo();
  const [isSelectionMode, setIsSelectionMode] = createSignal(false);
  const [selectedIds, setSelectedIds] = createSignal<Set<string>>(new Set());

  const toggleSelectionMode = () => {
    setIsSelectionMode(!isSelectionMode());
    setSelectedIds(new Set<string>());
  };

  const toggleItemSelection = (id: string) => {
    const next = new Set(selectedIds());
    if (next.has(id)) {
      next.delete(id);
    } else {
      next.add(id);
    }
    setSelectedIds(next);
  };

  const handleBulkDelete = () => {
    const dbInstance = options.drizzle();
    if (!dbInstance || selectedIds().size === 0) {
      return;
    }

    const idsToDelete = [...selectedIds()];

    // Exit animation for all selected items
    options.setExitingIds((prev) => new Set([...prev, ...idsToDelete]));

    setTimeout(() => {
      void (async () => {
        try {
          await Promise.all(idsToDelete.map(async (id) => deleteTodoMutation(dbInstance, id)));
          registerUndo(t(msg`Tasks moved to trash`), async () => {
            await Promise.all(idsToDelete.map(async (id) => restoreTodoMutation(dbInstance, id)));
          });
          setSelectedIds(new Set<string>());
          setIsSelectionMode(false);
        } catch (err: unknown) {
          console.error("Error in bulk delete:", err instanceof Error ? err.message : String(err));
        } finally {
          options.setExitingIds((prev) => {
            const next = new Set(prev);
            for (const id of idsToDelete) {
              next.delete(id);
            }
            return next;
          });
        }
      })();
    }, EXIT_DURATION_MS);
  };

  const handleBulkImportant = async (important: boolean) => {
    const dbInstance = options.drizzle();
    if (!dbInstance || selectedIds().size === 0) {
      return;
    }
    try {
      await Promise.all(
        [...selectedIds()].map(async (id) =>
          updateTodoMutation(dbInstance, id, { important: important ? 1 : 0 }),
        ),
      );
      setSelectedIds(new Set<string>());
      setIsSelectionMode(false);
    } catch (err: unknown) {
      console.error("Error in bulk important:", err instanceof Error ? err.message : String(err));
    }
  };

  const handleBulkMove = async (listId: string) => {
    const dbInstance = options.drizzle();
    if (!dbInstance || selectedIds().size === 0) {
      return;
    }
    try {
      await Promise.all(
        [...selectedIds()].map(async (id) =>
          updateTodoMutation(dbInstance, id, { list_id: listId }),
        ),
      );
      setSelectedIds(new Set<string>());
      setIsSelectionMode(false);
    } catch (err: unknown) {
      console.error("Error in bulk move:", err instanceof Error ? err.message : String(err));
    }
  };

  return {
    isSelectionMode,
    selectedIds,
    setSelectedIds,
    toggleSelectionMode,
    toggleItemSelection,
    handleBulkDelete,
    handleBulkImportant,
    handleBulkMove,
  };
}
